import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Botpoison from '@botpoison/browser';
import Hero from '../components/Hero';
import heroImage from '../assets/hero.jpg';
import donkeyLogo from '../assets/donkey_logo.png';
import logo from '../assets/logo.jpg';
import './PageStyles.css';

// Create a new Botpoison instance with the public key
const botpoison = new Botpoison({
  publicKey: 'pk_89cb49f9-1881-4778-965a-c2fd86dd027a'
});

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Process a challenge with Botpoison
      const { solution } = await botpoison.challenge();

      // Submit to Formspark with botpoison solution
      await axios.post('https://submit-form.com/DJYuJZoxU', {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
        _botpoison: solution,
      });

      setIsSuccess(true);
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      setError('There was an error submitting your message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendAnother = () => {
    setIsSuccess(false);
    setError('');
  };

  // Add JSON-LD structured data for SEO
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BarOrPub",
      "name": "Burbank's",
      "image": "https://yourdomain.com/images/your-bar-image.jpg",
      "@id": "https://yourdomain.com/contact-burbank-bar-pool-hall",
      "url": "https://yourdomain.com",
      "telephone": "+14083202466",
      "email": "burbanksbandb@gmail.com",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "398 S Bascom Ave",
        "addressLocality": "San Jose",
        "addressRegion": "CA",
        "postalCode": "95128",
        "addressCountry": "US"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 37.3272,
        "longitude": -121.9316
      },
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday"],
          "opens": "13:00",
          "closes": "00:00"
        },
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
          "opens": "12:00",
          "closes": "00:00"
        }
      ],
      "priceRange": "$$",
      "servesCuisine": ["American", "Bar Food"],
      "menu": "https://yourdomain.com/menu",
      "sameAs": [
        "https://www.facebook.com/BurbanksBitesAndBilliards",
        "https://www.instagram.com/burbanksbitesandbilliards"
      ]
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  if (isSuccess) {
    return (
      <div className="page-container">
        <div className="success-container">
          <div className="success-content">
            <div className="success-icon">
              <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" fill="#10B981" />
                <path d="M8 12l2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h1>Thank You!</h1>
            <p className="success-message">
              Your message has been sent successfully. We'll get back to you as soon as possible!
            </p>
            <div className="success-actions">
              <button onClick={handleSendAnother} className="send-another-btn">
                Send Another Message
              </button>
              <button onClick={() => window.location.href = '/'} className="home-btn">
                Back to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Hero
        title="Contact Us"
        image={heroImage}
        height="40vh"
        useClipPath={true}
        chevronLogo={donkeyLogo}
      />

      <div className="contact-container">
        <div className="contact-info">
          <div className="info-section">
            <h2>Get in Touch</h2>
            <div className="business-header">
              <img src={logo} alt="Burbank's" className="contact-logo" />
            </div>
            <div className="contact-details">
              <div className="contact-item">
                <h3>📍 Location</h3>
                <a
                  href="https://maps.app.goo.gl/u9wULdZfpq7KcNp66"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  398 S Bascom Ave<br />San Jose, CA 95128
                </a>
              </div>

              <div className="contact-item">
                <h3>📞 Phone</h3>
                <a href="tel:+14083202466">(408) 320-2466</a>
              </div>

              <div className="contact-item">
                <h3>✉️ Email</h3>
                <a href="mailto:burbanksbandb@gmail.com">burbanksbandb@gmail.com</a>
              </div>

              <div className="contact-item">
                <h3>🕒 Hours</h3>
                <p>
                  Monday - Tuesday: 1:00 PM - 12:00 AM<br />
                  Wednesday - Sunday: 12:00 PM - 12:00 AM
                </p>
              </div>

              <div className="contact-item">
                <h3>🌐 Follow Us</h3>
                <div className="social-links">
                  <a href="https://www.facebook.com/BurbanksBitesAndBilliards" target="_blank" rel="noopener noreferrer">
                    📘 Facebook
                  </a>
                  <a href="https://www.instagram.com/burbanksbitesandbilliards" target="_blank" rel="noopener noreferrer">
                    📷 Instagram
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="map-section">
            <h3>Find Us</h3>
            <div className="map-container">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3172.6675893920247!2d-121.93405542356491!3d37.32719597220585!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x808fcb1c3c357b13%3A0x705d7a7c32270f57!2s398%20S%20Bascom%20Ave%2C%20San%20Jose%2C%20CA%2095128!5e0!3m2!1sen!2sus!4v1710906547959!5m2!1sen!2sus"
                width="100%"
                height="450"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Burbank's Location"
              ></iframe>
            </div>
          </div>
        </div>

        <div className="contact-form-section">
          <h2>Send Us a Message</h2>

          {error && (
            <div className="error-message">
              <p>{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="contact-form">
            <div className="form-group">
              <label htmlFor="name">Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone">Phone</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                disabled={isSubmitting}
              />
            </div>

            <div className="form-group">
              <label htmlFor="message">Message *</label>
              <textarea
                id="message"
                name="message"
                rows={5}
                value={formData.message}
                onChange={handleChange}
                required
                disabled={isSubmitting}
              ></textarea>
            </div>

            <button type="submit" className="submit-btn" disabled={isSubmitting}>
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Contact; 